import { WidgetProps } from '@highimpact-front/remote-widget-core';
import { isPoint } from '@highimpact-frontend/map-geometry';
import {
  GenericErrorBoundary,
  LeafletFitBounds,
} from '@highimpact/dashboard-ui';
import { PermissionDecorator } from '@highimpact/react-decorators';
import L from 'leaflet';
import { forwardRef, type LegacyRef } from 'react';
import {
  MapContainer,
  Marker,
  Polygon,
  Popup,
  SVGOverlay,
  TileLayer,
} from 'react-leaflet';
import { useDashboardPermissionScope } from '../../../../hooks/useDashboardPermissionScope';
import { useMapSiteLayers } from '../../../../hooks/useMapSiteLayers';
import { pin } from '../../../../internal/map/mapPin';
import { useDashboardLibraryDispatch } from '../../../../runtime/DashboardLibraryProvider';
import { setDeleteWidgetConfirmation } from '../../../../state/dashboard/slice';
import NameHandlerComponent from '../../../NameHandlerComponent';
import Widget from '../../commons/Widget';
import { WidgetDragHandle } from '../../commons/WidgetDragHandle';
import { WidgetToolbar } from '../../commons/WidgetToolbar';
import type { Datasource } from './mapTypes';
import { useMapInstanceRegistration } from './useMapInstanceRegistration';

export type { Datasource, MarkerType } from './mapTypes';

/**
 * Component props.
 */
interface MapWidgetProps extends WidgetProps {
  name: string;
  dataSource: Datasource[];
  gridId: number;
  gridType: 'reports' | 'grids';
}

function Map(
  {
    dashboardElementId,
    name,
    dataSource,
    className,
    non_removable,
    gridType,
    gridId,
    ...props
  }: MapWidgetProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const dispatch = useDashboardLibraryDispatch();
  const { site_id, project_id } = useDashboardPermissionScope();
  const { layers, loading, error } = useMapSiteLayers(dataSource);

  useMapInstanceRegistration({
    dashboardElementId,
    widgetId: props.widget_id,
    enabled: !loading && !error,
  });

  const mapLayers = layers
    .map((layerInfo, index) => {
      if (!layerInfo.geometry.coordinates) {
        return null;
      }

      if (layerInfo.geometry.type === 'POINT') {
        const [lat, lng] = layerInfo.geometry.coordinates;

        return (
          <Marker
            key={index}
            position={[parseFloat(lat.toString()), parseFloat(lng.toString())]}
            icon={pin}
          >
            <Popup>{layerInfo.info}</Popup>
          </Marker>
        );
      }

      if (layerInfo.geometry.type === 'POLYGON') {
        const coordinates = layerInfo.geometry.coordinates;

        return (
          <Polygon key={index} positions={coordinates as [number, number][]} />
        );
      }

      if (layerInfo.geometry.type === 'TEXT') {
        const bounds = layerInfo.geometry.coordinates;

        return (
          <SVGOverlay
            key={index}
            attributes={{ stroke: 'black' }}
            bounds={bounds}
          >
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="white"
              style={{ display: 'flex' }}
            />
            <text x="10%" y="50%" stroke="black" style={{ marginLeft: '0' }}>
              Ceci est un text
            </text>
          </SVGOverlay>
        );
      }

      return null;
    })
    .filter((jsx) => jsx !== null);

  const fitBoundsLayer = layers
    .filter((layerInfo) => layerInfo.geometry.coordinates)
    .map((layerInfo) => layerInfo.geometry)
    .filter(isPoint);

  return (
    <Widget
      dashboardElementId={dashboardElementId}
      style={{ width: '100%', height: '100%' }}
      ref={ref}
      {...props}
      className={`map ${className || ''}`}
    >
      <GenericErrorBoundary>
        <PermissionDecorator
          item="dashboard_nav_bar_widget"
          project_id={Number.isNaN(project_id) ? undefined : Number(project_id)}
          site_id={site_id}
          extra={{ dashboard_id: gridId }}
        >
          <div className="widget-nav">
            <p style={{ display: 'flex' }}>
              <PermissionDecorator item="dashboard_move_widget">
                <WidgetDragHandle />
              </PermissionDecorator>
              <PermissionDecorator item="dashboard_rename_widget">
                <NameHandlerComponent
                  widgetId={dashboardElementId}
                  dashboardElementId={props.widget_id}
                  dashboardId={gridId}
                  dashboardType={gridType}
                />
              </PermissionDecorator>
            </p>
            {!non_removable && (
              <div className="nav-icon">
                <WidgetToolbar
                  dashboardElementId={dashboardElementId}
                  dashboardId={gridId}
                  // TODO: replace
                  platformActions={{
                    delete: {
                      visible: true,
                      tooltipText: "Supprimer l'élément",
                      permissionItem: 'dashboard_delete_widget',
                      onClick: () =>
                        dispatch(
                          setDeleteWidgetConfirmation({
                            state: 'open',
                            dashboardElementId: dashboardElementId,
                            widgetName: name,
                          })
                        ),
                    },
                  }}
                />
              </div>
            )}
          </div>
        </PermissionDecorator>
        {loading ? (
          <p>Chargement de la carte...</p>
        ) : error ? (
          <p>Impossible de charger les sites cartographiés.</p>
        ) : (
          <MapContainer
            style={{ width: '100%', height: '100%' }}
            center={[46.651522, 2.390475]}
            zoom={6}
            zoomControl={false}
            crs={L.CRS.EPSG3857}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LeafletFitBounds geometries={fitBoundsLayer} />
            {mapLayers}
          </MapContainer>
        )}
      </GenericErrorBoundary>
    </Widget>
  );
}

export default forwardRef(Map);
